import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Typography } from '@mui/material';
import * as api from './articleTypesApi';
import type { ArticleType } from './articleTypesApi';
import { useDetailView } from '../common/useDetailView';
import { sx14, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    articleTypeId: number | null;
}

export function ArticleTypeView({ articleTypeId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<ArticleType>();

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            if (articleTypeId === null) {
                detail.setItem({ id: 0, articleTypeName: '' });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getArticleType(articleTypeId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [articleTypeId]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        await detail.withSaving(async () => {
            const response = await api.saveArticleType(detail.item!);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    return (
        <DetailViewShell<ArticleType>
            title={detail.item?.name ?? detail.item?.articleTypeName ?? ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={!!detail.item?.articleTypeName && detail.item.articleTypeName.length > 0}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (
                <>
                    <FormRow label={t('article.articleTypeName')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.articleTypeName}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={item.articleTypeName}
                                onChange={e => detail.update('articleTypeName', e.target.value)}
                                error={!item.articleTypeName || item.articleTypeName.length === 0} />
                        )}
                    </FormRow>

                    <FormRow label={t('article.articleSystemType')}>
                        <Typography sx={sx14}>{item.articleSystemType}</Typography>
                    </FormRow>
                </>
            )}
        </DetailViewShell>
    );
}
